import React from "react";
import { markSeenOnServer } from "restApi/seenApi";

const readLocal = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeLocal = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // A cache only; the server has the real value.
  }
};

// When the signed-in user last looked at a page ("upcoming" or "proposals"),
// kept on their account so the "new or changed" dot agrees across devices.
//
//  - `lastSeenAt` stays null until the first answer from the server, so no dot
//    flashes up from a guess.
//  - `sync(seen)` takes a response from restApi/seenApi.fetchSeen. A never-set
//    value becomes the baseline (now), so everything that already exists isn't
//    flagged as new.
//  - `markSeen()` records "seen now" on the server, updating the dot at once.
//  - `onAdvanced` runs when the time moves forward past what this device last
//    knew - i.e. the page was opened here or on another device - so the caller
//    can drop its app-icon badge and delivered notifications.
//
// localStorage keeps this device's last-known value (under `storageKey`, the
// key it has always used) purely so a fresh start can tell the server's value
// moved on while the app was closed.
export default function useSeenTime({ what, storageKey, onAdvanced }) {
  const [lastSeenAt, setLastSeenAt] = React.useState(null);
  const known = React.useRef(readLocal(storageKey));
  const marking = React.useRef(false);
  const baselining = React.useRef(false);
  const onAdvancedRef = React.useRef(onAdvanced);
  onAdvancedRef.current = onAdvanced;

  const apply = React.useCallback(
    (iso) => {
      const previous = known.current;
      known.current = iso;
      writeLocal(storageKey, iso);
      setLastSeenAt(iso);
      if (previous && new Date(iso) > new Date(previous) && onAdvancedRef.current) {
        onAdvancedRef.current();
      }
    },
    [storageKey]
  );

  const sync = React.useCallback(
    (seen) => {
      // While our own "seen now" is on its way, the server still shows the old
      // time; don't let that put the dot back.
      if (!seen || marking.current) return;
      const value = seen[what];
      if (value) {
        apply(value);
        return;
      }
      if (baselining.current) return;
      baselining.current = true;
      markSeenOnServer(what)
        .then((data) => apply(data[what]))
        .catch(() => {})
        .finally(() => {
          baselining.current = false;
        });
    },
    [what, apply]
  );

  const markSeen = React.useCallback(() => {
    const optimistic = new Date().toISOString();
    marking.current = true;
    known.current = optimistic;
    writeLocal(storageKey, optimistic);
    setLastSeenAt(optimistic);
    markSeenOnServer(what)
      .then((data) => {
        if (data[what]) {
          known.current = data[what];
          writeLocal(storageKey, data[what]);
          setLastSeenAt(data[what]);
        }
      })
      .catch(() => {})
      .finally(() => {
        marking.current = false;
      });
  }, [what, storageKey]);

  return { lastSeenAt, sync, markSeen };
}
